import React, { useState, useEffect } from 'react';
import { reservesAPI } from '../services/api';
import './Reserves.css';

interface Overview {
  totalReserves: string;
  currentProduction: string;
  provenReserves: string;
  probableReserves: string;
  primaryBasin: string;
  waterDepth: string;
}

interface Block {
  name: string;
  operator: string;
  partners: string[];
  discoveries: number;
  estimatedReserves: string;
  status: string;
  location: string;
}

interface Company {
  name: string;
  role: string;
  website: string;
  description: string;
  projects: string[];
  contact: {
    phone: string;
    email: string;
    address: string;
  };
}

interface Ownership {
  title: string;
  description: string;
  structure: {
    government: {
      role: string;
      share: string;
      responsibilities: string[];
    };
    operators: {
      role: string;
      share: string;
      responsibilities: string[];
    };
  };
  revenueSharing: string[];
  localContent: string[];
}

const Reserves: React.FC = () => {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [ownership, setOwnership] = useState<Ownership | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReservesData();
  }, []);

  const fetchReservesData = async () => {
    try {
      setLoading(true);
      const [overviewRes, blocksRes, companiesRes, ownershipRes] = await Promise.all([
        reservesAPI.getOverview(),
        reservesAPI.getBlocks(),
        reservesAPI.getCompanies(),
        reservesAPI.getOwnership()
      ]);

      setOverview(overviewRes.data.data);
      setBlocks(blocksRes.data.data);
      setCompanies(companiesRes.data.data);
      setOwnership(ownershipRes.data.data);
    } catch (err) {
      setError('Error loading reserves data. Please try again later.');
      console.error('Error fetching reserves:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyWebsite = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="reserves-page">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading reserves data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reserves-page">
        <div className="container">
          <div className="error">
            <h2>Error Loading Reserves Data</h2>
            <p>{error}</p>
            <button onClick={fetchReservesData} className="btn btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reserves-page">
      <div className="container">
        {/* Header */}
        <div className="reserves-header">
          <h1>Oil & Gas Reserves</h1>
          <p>Explore Guyana's vast oil and gas reserves, key companies, and ownership structure</p>
        </div>

        {/* Overview Section */}
        {overview && (
          <section className="overview-section">
            <h2>Reserves Overview</h2>
            <div className="overview-grid">
              <div className="overview-card">
                <h3>Total Reserves</h3>
                <p className="value">{overview.totalReserves}</p>
              </div>
              <div className="overview-card">
                <h3>Current Production</h3>
                <p className="value">{overview.currentProduction}</p>
              </div>
              <div className="overview-card">
                <h3>Proven Reserves</h3>
                <p className="value">{overview.provenReserves}</p>
              </div>
              <div className="overview-card">
                <h3>Probable Reserves</h3>
                <p className="value">{overview.probableReserves}</p>
              </div>
              <div className="overview-card">
                <h3>Primary Basin</h3>
                <p className="value">{overview.primaryBasin}</p>
              </div>
              <div className="overview-card">
                <h3>Water Depth</h3>
                <p className="value">{overview.waterDepth}</p>
              </div>
            </div>
          </section>
        )}

        {/* Blocks Section */}
        <section className="blocks-section">
          <h2>Offshore Blocks</h2>
          <div className="blocks-grid">
            {blocks.map((block, index) => (
              <div key={index} className="block-card">
                <h3>{block.name}</h3>
                <div className="block-details">
                  <p><strong>Operator:</strong> {block.operator}</p>
                  <p><strong>Discoveries:</strong> {block.discoveries}</p>
                  <p><strong>Estimated Reserves:</strong> {block.estimatedReserves}</p>
                  <p><strong>Status:</strong> {block.status}</p>
                  <p><strong>Location:</strong> {block.location}</p>
                </div>
                <div className="partners">
                  <h4>Partners:</h4>
                  <ul>
                    {block.partners.map((partner, idx) => (
                      <li key={idx}>{partner}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Companies Section */}
        <section className="companies-section">
          <h2>Key Companies</h2>
          <div className="companies-grid">
            {companies.map((company, index) => (
              <div key={index} className="company-card">
                <h3>{company.name}</h3>
                <p className="role">{company.role}</p>
                <p className="description">{company.description}</p>
                
                <div className="projects">
                  <h4>Projects:</h4>
                  <ul>
                    {company.projects.map((project, idx) => (
                      <li key={idx}>{project}</li>
                    ))}
                  </ul>
                </div>

                <div className="contact-info">
                  <h4>Contact Information:</h4>
                  <p><strong>Phone:</strong> {company.contact.phone}</p>
                  <p><strong>Email:</strong> {company.contact.email}</p>
                  <p><strong>Address:</strong> {company.contact.address}</p>
                </div>

                <button
                  onClick={() => handleCompanyWebsite(company.website)}
                  className="btn btn-primary"
                >
                  Visit Website
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Ownership Section */}
        {ownership && (
          <section className="ownership-section">
            <h2>{ownership.title}</h2>
            <p className="ownership-description">{ownership.description}</p>
            
            <div className="ownership-structure">
              <div className="structure-card">
                <h3>{ownership.structure.government.role}</h3>
                <p><strong>Share:</strong> {ownership.structure.government.share}</p>
                <h4>Responsibilities:</h4>
                <ul>
                  {ownership.structure.government.responsibilities.map((resp, index) => (
                    <li key={index}>{resp}</li>
                  ))}
                </ul>
              </div>
              
              <div className="structure-card">
                <h3>{ownership.structure.operators.role}</h3>
                <p><strong>Share:</strong> {ownership.structure.operators.share}</p>
                <h4>Responsibilities:</h4>
                <ul>
                  {ownership.structure.operators.responsibilities.map((resp, index) => (
                    <li key={index}>{resp}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="revenue-sharing">
              <h3>Revenue Sharing</h3>
              <ul>
                {ownership.revenueSharing.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="local-content">
              <h3>Local Content</h3>
              <ul>
                {ownership.localContent.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Reserves;
